/**
 * Shared vocabulary and decision logic for the site gate.
 *
 * Named for the mechanism, not the message: the gate currently fronts a
 * pre-launch site, but the same decision applies to a maintenance window. Only
 * the destination path below is allowed to be specific about which.
 *
 * Kept free of Nuxt and h3 imports on purpose: both the Vue route middleware
 * and the nitro server middleware consume it, and the decision itself is
 * exhaustively unit-tested without mocking either runtime.
 */

/**
 * Where gated routes are sent.
 *
 * Still `/coming-soon` because that is genuinely what the page is today. A
 * maintenance mode would add its own page alongside this one rather than
 * rename it, so the URL visitors see and share stays honest.
 */
export const SITE_GATE_PATH = '/coming-soon';

/** Marks a browser session as holding preview access. */
export const PREVIEW_COOKIE_NAME = 'vw_preview';

/** Query param carrying the secret preview token, or `off` to sign out. */
export const PREVIEW_QUERY_PARAM = 'preview';

/** Value of `?preview=` that clears preview access. */
export const PREVIEW_OFF_VALUE = 'off';

/**
 * Pages that stay reachable while the gate is closed: the gate page itself,
 * plus the public pages its footer links to. Everything else is gated.
 */
export const SITE_GATE_ALLOWLIST = [
  SITE_GATE_PATH,
  '/contact',
  '/privacy-policy',
  '/terms-of-use',
] as const;

/** Drops a single trailing slash so `/contact/` and `/contact` compare equal. */
function normalisePath(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** Whether two paths refer to the same route, ignoring a trailing slash. */
export function isSamePath(a: string, b: string): boolean {
  return normalisePath(a) === normalisePath(b);
}

/**
 * Exact-match against the allowlist, tolerating a trailing slash.
 *
 * Deliberately not a prefix match: `/contact-us` is a different page (the Help
 * Centre row) and must stay gated even though `/contact` is public.
 */
export function isAllowlistedPath(path: string): boolean {
  return (SITE_GATE_ALLOWLIST as readonly string[]).includes(
    normalisePath(path),
  );
}

export interface SiteGateRedirectInput {
  /** The requested path, without query string or hash. */
  path: string;
  /** Whether the gate is closed, per the CMS. */
  gateEnabled: boolean;
  /** Whether this session holds a valid preview cookie. */
  hasPreviewAccess: boolean;
}

/**
 * Returns the path to redirect to, or `null` to let the request through.
 */
export function resolveSiteGateRedirect({
  path,
  gateEnabled,
  hasPreviewAccess,
}: SiteGateRedirectInput): string | null {
  if (!gateEnabled) {
    // With the gate open the page is dead weight, and leaving it addressable
    // would let it be indexed alongside the real home page.
    return isSamePath(path, SITE_GATE_PATH) ? '/' : null;
  }

  if (hasPreviewAccess) return null;
  if (isAllowlistedPath(path)) return null;

  return SITE_GATE_PATH;
}
